import * as React from "react";
import { cn } from "@app/ui/lib/Utils";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** 입력 왼쪽에 겹쳐 붙는 슬롯(아이콘·툴팁 트리거 등). 있으면 왼쪽 패딩이 늘어난다. */
  leftSlot?: React.ReactNode;
  /** 입력 오른쪽에 겹쳐 붙는 슬롯. 있으면 오른쪽 패딩이 늘어난다. */
  rightSlot?: React.ReactNode;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, leftSlot, rightSlot, ...props }, ref) => {
    const input = (
      <input
        type={type}
        ref={ref}
        className={cn(
          "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:bg-muted disabled:cursor-not-allowed disabled:opacity-50",
          leftSlot && "pl-9",
          rightSlot && "pr-9",
          className
        )}
        {...props}
      />
    );

    // 슬롯 유무와 무관하게 항상 같은 div 래퍼를 반환한다. 반환 엘리먼트 타입이
    // input↔div 로 바뀌면 React 가 <input> 노드를 언마운트/재마운트해 포커스가 날아가고
    // (모바일에선 키보드가 내려간다), 슬롯이 입력값에 따라 토글되는 필드에서 이게 터진다.
    // 슬롯이 없을 땐 display:contents 로 래퍼를 레이아웃에서 투명하게 만들어, 순수 input
    // 소비처의 기존 레이아웃을 그대로 유지한다. 슬롯이 있을 때만 relative w-full 로 아이콘
    // 위치 컨텍스트를 준다. (className 만 바뀌는 건 remount 를 유발하지 않는다.)
    return (
      <div className={cn(leftSlot || rightSlot ? "relative w-full" : "contents")}>
        {leftSlot && (
          // z-10: input 위에 얹혀 클릭·hover 를 받게 한다. inset-y-0 로 input 높이에 맞춰 세로 가운데 정렬.
          <span className="absolute inset-y-0 left-0 z-10 flex items-center pl-2 text-muted-foreground">
            {leftSlot}
          </span>
        )}
        {input}
        {rightSlot && (
          <span className="absolute inset-y-0 right-0 z-10 flex items-center pr-2 text-muted-foreground">
            {rightSlot}
          </span>
        )}
      </div>
    );
  }
);
Input.displayName = "Input";

export { Input };
